import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  FaClipboardList,
  FaSearch,
  FaVideo,
  FaFileSignature,
  FaDollarSign,
  FaCogs,
  FaRocket,
  FaExternalLinkAlt,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import "../styles/HowItWorks.css";

function GuidelinesModal({ isOpen, onClose }) {
  useEffect(() => {
    if (!isOpen) return;

    const onEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="irs-modal-overlay" onClick={onClose}>
      <div
        className="irs-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="irs-guidelines-title"
      >
        <button
          className="modal-close"
          onClick={onClose}
          aria-label="Close guidelines"
          type="button"
        >
          ×
        </button>

        <h2 id="irs-guidelines-title">501(c)(3) Qualification Guidelines</h2>

        <div className="rules-grid">
          <div className="rules-section allowed">
            <h3>
              <FaCheck /> Allowable Activities
            </h3>
            <ul>
              <li>Relief of poverty and distress</li>
              <li>Educational advancement</li>
              <li>Scientific research</li>
              <li>Public health initiatives</li>
              <li>Community improvement</li>
              <li>Arts and cultural programs</li>
              <li>Religious activities</li>
              <li>Environmental conservation</li>
            </ul>
          </div>

          <div className="rules-section prohibited">
            <h3>
              <FaTimes /> Prohibited Activities
            </h3>
            <ul>
              <li>Political campaign involvement</li>
              <li>Substantial lobbying</li>
              <li>Private benefit distribution</li>
              <li>Illegal activities</li>
              <li>For-profit operations</li>
              <li>Insider transactions</li>
              <li>Partisan activities</li>
              <li>Unrelated business income</li>
            </ul>
          </div>
        </div>

        <div className="rules-footer">
          <a
            href="https://www.irs.gov/charities-non-profits/charitable-organizations/exemption-requirements-501c3-organizations"
            target="_blank"
            rel="noopener noreferrer"
            className="irs-link"
          >
            <FaExternalLinkAlt /> View Full IRS Guidelines
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function HowItWorks() {
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);

  const smoothScrollTo = (selector, offset = 80) => {
    const el =
      document.querySelector(selector) ||
      document.getElementById(selector.replace("#", ""));
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <>
      <section className="howitworks-section" aria-labelledby="how-title">
        <div className="howitworks-bg" aria-hidden="true" />
        <div className="how-container">
          <h2 id="how-title" className="how-title">
            How It Works
          </h2>
          <p className="how-subtitle">
            A simple, guided path from application to launch — usually in days, not months.
          </p>

          <ol className="how-steps" role="list">
            <li className="how-step">
              <div className="how-marker" aria-hidden="true">
                <span className="how-num">1</span>
                <span className="how-line" />
              </div>

              <Link
                to="/apply"
                className="how-card how-card--link"
                role="link"
                aria-label="Apply now"
              >
                <div className="how-icon">
                  <FaClipboardList />
                </div>
                <div className="how-copy">
                  <h3 className="how-step-title">Apply</h3>
                  <p className="how-step-desc">Submit your project application in minutes.</p>
                </div>
              </Link>
            </li>

            <li className="how-step">
              <div className="how-marker" aria-hidden="true">
                <span className="how-num">2</span>
                <span className="how-line" />
              </div>

              <div className="how-card">
                <div className="how-icon">
                  <FaSearch />
                </div>
                <div className="how-copy">
                  <h3 className="how-step-title">Review</h3>
                  <p className="how-step-desc">
                    We confirm mission alignment within 501(c)(3) guidelines.{" "}
                    <button
                      type="button"
                      className="guidelines-link"
                      onClick={() => setShowGuidelinesModal(true)}
                      aria-label="Open 501(c)(3) guidelines"
                    >
                      View guidelines
                    </button>
                  </p>
                </div>
              </div>
            </li>

            <li className="how-step">
              <div className="how-marker" aria-hidden="true">
                <span className="how-num">3</span>
                <span className="how-line" />
              </div>
              <div className="how-card">
                <div className="how-icon">
                  <FaVideo />
                </div>
                <div className="how-copy">
                  <h3 className="how-step-title">Meet</h3>
                  <p className="how-step-desc">
                    Join a virtual intro call to discuss questions and next steps.
                  </p>
                </div>
              </div>
            </li>

            <li className="how-step">
              <div className="how-marker" aria-hidden="true">
                <span className="how-num">4</span>
                <span className="how-line" />
              </div>
              <div className="how-card">
                <div className="how-icon">
                  <FaFileSignature />
                </div>
                <div className="how-copy">
                  <h3 className="how-step-title">Sign</h3>
                  <p className="how-step-desc">Execute the fiscal sponsorship agreement.</p>
                </div>
              </div>
            </li>

            <li className="how-step">
              <div className="how-marker" aria-hidden="true">
                <span className="how-num">5</span>
                <span className="how-line" />
              </div>
              <div className="how-card">
                <div className="how-icon">
                  <FaDollarSign />
                </div>
                <div className="how-copy">
                  <h3 className="how-step-title">Open</h3>
                  <p className="how-step-desc">Fund your Account</p>
                </div>
              </div>
            </li>

            <li className="how-step">
              <div className="how-marker" aria-hidden="true">
                <span className="how-num">6</span>
                <span className="how-line" />
              </div>
              <div className="how-card">
                <div className="how-icon">
                  <FaCogs />
                </div>
                <div className="how-copy">
                  <h3 className="how-step-title">Onboard</h3>
                  <p className="how-step-desc">
                    Get set up on our platform, tools, and processes.
                  </p>
                </div>
              </div>
            </li>

            <li className="how-step">
              <div className="how-marker" aria-hidden="true">
                <span className="how-num">7</span>
                <span className="how-line" />
              </div>
              <div className="how-card">
                <div className="how-icon">
                  <FaRocket />
                </div>
                <div className="how-copy">
                  <h3 className="how-step-title">Launch</h3>
                  <p className="how-step-desc">
                    Start collecting donations and operating your project.
                  </p>
                </div>
              </div>
            </li>
          </ol>

          <div className="how-ctas">
            <Link to="/apply" className="how-cta how-cta--primary">
              Apply Now
            </Link>
            <button
              type="button"
              className="how-cta how-cta--ghost"
              onClick={() => smoothScrollTo("#causes-overview", 100)}
            >
              What We Look For
            </button>
          </div>
        </div>
      </section>

      <GuidelinesModal
        isOpen={showGuidelinesModal}
        onClose={() => setShowGuidelinesModal(false)}
      />
    </>
  );
}